import React from "react";
import { useApp } from "../../context/AppContext";
import GlassCard from "../../components/ui/GlassCard";
import StatCard from "../../components/ui/StatCard";
import Button from "../../components/ui/Button";
import Badge from "../../components/ui/Badge";
import ProgressBar from "../../components/ui/ProgressBar";
import PageShell from "../../components/layout/PageShell";

interface Props { orgId?: string; onNavigate: (tab: string) => void; }

const OrgAdminDashboard: React.FC<Props> = ({ orgId, onNavigate }) => {
  const { orgs, users, exams, results, assignments } = useApp();
  const org        = orgs.find(o => o.id === orgId);
  const candidates = users.filter(u => u.role === "candidate" && u.orgId === orgId);
  const orgExams   = exams.filter(e => e.orgId === orgId);
  const orgAssigns = assignments.filter(a => a.orgId === orgId);

  return (
    <PageShell title={org?.name ?? "Dashboard"} subtitle="Organisation assessment overview">
      {/* Brand header */}
      <GlassCard className="p-5">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-white font-extrabold text-2xl shadow-lg"
            style={{ background: org?.primaryColor ?? "#6366f1" }}>
            {org?.name[0]}
          </div>
          <div>
            <h2 className="text-xl font-extrabold text-gray-800">{org?.name}</h2>
            <p className="text-sm text-gray-500">{org?.domain}</p>
            <div className="flex gap-2 mt-1">
              <Badge text={org?.status ?? "active"} />
              <Badge text={org?.plan ?? "pro"} />
            </div>
          </div>
        </div>
      </GlassCard>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Candidates"  value={candidates.length}                                      icon="👤" color="#6366f1" onClick={() => onNavigate("candidates")} />
        <StatCard label="Active Exams"value={orgExams.filter(e=>e.status==="active").length}         icon="📋" color="#10b981" onClick={() => onNavigate("assignments")} />
        <StatCard label="Assigned"    value={orgAssigns.length}                                      icon="📌" color="#f59e0b" onClick={() => onNavigate("assignments")} />
        <StatCard label="Completed"   value={orgAssigns.filter(a=>a.status==="completed").length}    icon="✅" color="#ec4899" onClick={() => onNavigate("results")} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Candidates list */}
        <GlassCard className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-extrabold text-gray-800">Candidates</h3>
            <Button size="sm" variant="secondary" onClick={() => onNavigate("candidates")}>View All →</Button>
          </div>
          <div className="space-y-3">
            {candidates.slice(0, 5).map(c => (
              <div key={c.id} className="flex items-center gap-3 p-2.5 rounded-xl bg-white/40 hover:bg-white/60 transition-colors">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-300 to-purple-400 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                  {c.name[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-800 truncate">{c.name}</p>
                  <p className="text-xs text-gray-400">{c.email}</p>
                </div>
                <Badge text={c.status} />
              </div>
            ))}
            {candidates.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No candidates yet.</p>}
          </div>
        </GlassCard>

        {/* Exam performance */}
        <GlassCard className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-extrabold text-gray-800">Exam Performance</h3>
            <Button size="sm" variant="secondary" onClick={() => onNavigate("results")}>View →</Button>
          </div>
          <div className="space-y-4">
            {results.map(r => {
              const exam = exams.find(e => e.id === r.examId);
              const col  = r.percentage >= 70 ? "#10b981" : r.percentage >= 50 ? "#f59e0b" : "#ef4444";
              return (
                <div key={r.id}>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-semibold text-gray-700 truncate max-w-xs">{exam?.title}</span>
                    <span className="text-sm font-extrabold" style={{ color: col }}>{r.percentage}%</span>
                  </div>
                  <ProgressBar value={r.percentage} max={100} color={col} height={7} />
                </div>
              );
            })}
            {results.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No results yet.</p>}
          </div>
        </GlassCard>
      </div>

      {/* Exams */}
      <GlassCard className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-extrabold text-gray-800">Exams</h3>
          <Button size="sm" variant="secondary" onClick={() => onNavigate("assignments")}>Assign →</Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {orgExams.map(exam => (
            <div key={exam.id} className="p-4 rounded-xl bg-white/40 border border-white/30">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Badge text={exam.status} />
                <Badge text={exam.type} />
              </div>
              <p className="font-bold text-gray-800 text-sm">{exam.title}</p>
              <p className="text-xs text-gray-400 mt-1">⏱ {exam.duration}min · 👥 {exam.assignedCount}</p>
            </div>
          ))}
          {orgExams.length === 0 && <p className="text-sm text-gray-400">No exams assigned to this organisation.</p>}
        </div>
      </GlassCard>
    </PageShell>
  );
};

export default OrgAdminDashboard;
